import React from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuGroupHeader,
  MenuItem,
  ToolbarButton,
  Spinner,
} from "@fluentui/react-components";
import {
  ChannelArrowLeft28Regular,
  DatabasePlugConnectedRegular,
  Person28Regular,
  ShieldLockRegular,
} from "@fluentui/react-icons";
import {
  useLogout,
  AnalyticsManager,
  AnalyticsConstants,
  ApiResponse,
  TCTeamsFxContext,
  PolarionTeamsContext,
  TeamsFxContext,
  Configuration,
  Constants,
  logger,
} from "@siemensplm/common_utils";
import { dialog } from "@microsoft/teams-js";
import { useCapabilitiesContext } from "../context/CapabilitiesContext";

const LoginToolbar: React.FunctionComponent = () => {
  const tcTeamsContext = React.useContext(TCTeamsFxContext);
  const teamsContext = React.useContext(TeamsFxContext);
  const polarionTeamsContext = React.useContext(PolarionTeamsContext);
  const i18n = tcTeamsContext.i18n;
  const logoutMutation = useLogout();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const teamcenterConfiguration = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterCode
  );
  const selectedSite = JSON.parse(
    window.localStorage.getItem(Constants.cacheSelectedSite) || "{}"
  );
  const { setDefaultCapability } = useCapabilitiesContext();
  const handleLogout = async () => {
    logger.logInformation("LoginToolbar: handleLogout called");
    try {
      setIsLoggingOut(true);
      const sessionId = tcTeamsContext.teamcenter?.session?.sessionId || "";
      const userUid = tcTeamsContext.teamcenter?.session?.userUid || "";
      await logoutMutation.mutateAsync({ sessionId, userUid });
      tcTeamsContext.teamcenter.logout();
      localStorage.removeItem(Constants.cacheSelectedSite);
      localStorage.removeItem(Constants.activeCapability);
      setDefaultCapability();
    } catch (error) {
      tcTeamsContext.failure.setError(error as ApiResponse["error"]);
      logger.logError(`LoginToolbar: Error during logout - ${error}`);
    } finally {
      setIsLoggingOut(false);
      logger.logInformation("LoginToolbar: handleLogout completed");
    }
  };
  const handlePolarionLogout = async () => {
    try {
      setIsLoggingOut(true);

      polarionTeamsContext.polarion.logout();
      window.localStorage.removeItem(Constants.activeCapability);
      window.localStorage.removeItem(Constants.cacheSelectedSite);
    } catch (error) {
      polarionTeamsContext.failure.setError(error as ApiResponse["error"]);
    } finally {
      setIsLoggingOut(false);
    }
  };
  const openDataPrivacyDialog = () => {
    const analyticsManager = AnalyticsManager.getInstance();
    analyticsManager.logEvent(AnalyticsConstants.cmdDataPrivacy);
    const taskInfo = {
      title: i18n.DataPrivacyText,
      url: window.location.origin + "/index.html#/dataprivacy",
      card: null,
      size: { height: 600, width: window.innerWidth > 1000 ? 650 : 350 },
    };
    dialog.url.open(taskInfo, () =>
      tcTeamsContext.queryClient.invalidateQueries()
    );
  };

  const showDataPrivacy = () => {
    let retVal: boolean = false;
    const localConfiguration: any =
      window.localStorage.getItem("configuration");
    if (localConfiguration) {
      const configurationObject: any = JSON.parse(localConfiguration);
      if (configurationObject) {
        if (configurationObject[Constants.tenantTelemetryOptIn]) {
          const telemetryOptInValue: boolean =
            configurationObject[Constants.tenantTelemetryOptIn];
          if (telemetryOptInValue === true) {
            retVal = true;
          }
        }
      }
    }
    return retVal;
  };

  const getThemeClass = () => {
    let retVal: string = "data-privacy-icon-default";
    if (teamsContext && teamsContext.themeString) {
      if (teamsContext.themeString === "dark") {
        retVal = "data-privacy-icon-dark";
      } else if (teamsContext.themeString === "contrast") {
        retVal = "data-privacy-icon-contrast";
      }
    }
    return retVal;
  };

  return (
    <>
      {isLoggingOut && (
        <div className="logout-spinner">
          <Spinner size="large" label={i18n.LoggingOut} />
        </div>
      )}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <ToolbarButton aria-label="More" icon={<Person28Regular />} />
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            <MenuGroupHeader>
              {teamsContext?.userInfo?.displayName}
            </MenuGroupHeader>
            {teamcenterConfiguration && teamcenterConfiguration.siteDisplay && (
              <MenuItem
                className="badge-for-environment"
                icon={<DatabasePlugConnectedRegular />}
              >
                {teamcenterConfiguration.siteDisplay}
              </MenuItem>
            )}
            {showDataPrivacy() ? (
              <MenuItem
                className={getThemeClass()}
                icon={<ShieldLockRegular />}
                onClick={openDataPrivacyDialog}
              >
                {i18n.DataPrivacyText}
              </MenuItem>
            ) : null}
            {selectedSite?.appName === Constants.Teamcenter && (
              <MenuItem
                icon={<ChannelArrowLeft28Regular />}
                onClick={handleLogout}
                disabled={!tcTeamsContext.teamcenter.isAuthenticated}
              >
                {i18n.LogoutTeamcenterText}
              </MenuItem>
            )}

            {selectedSite?.appName === Constants.Polarion && (
              <MenuItem
                icon={<ChannelArrowLeft28Regular />}
                onClick={handlePolarionLogout}
                disabled={!polarionTeamsContext.polarion.isAuthenticated}
              >
                {i18n.LogoutPolarionText}
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
    </>
  );
};

export default LoginToolbar;
